import { Suspense, useCallback, useRef } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { useOutletContext } from "react-router"
import { ArrowSquareOut } from "@phosphor-icons/react/dist/ssr"
import { graphql } from "relay-runtime"
import { Button } from "@/components/ui/button"
import { Drawer, DrawerBody, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer"
import { useClickOutside } from "@/hooks/useClickOutside"
import { formatDate } from "@/lib/format-date"
import { alternativeTriageDrawerChQuery } from "@/modules/patients/agenda/alternative-triage/__generated__/alternativeTriageDrawerChQuery.graphql"
import { alternativeTriageDrawerDeQuery } from "@/modules/patients/agenda/alternative-triage/__generated__/alternativeTriageDrawerDeQuery.graphql"
import { TimeWindowDrawer } from "@/modules/patients/agenda/time-window/time-window-drawer"
import { TimeWindowFormSchema } from "@/modules/patients/agenda/time-window/time-window-schema"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { ErrorFallback } from "@/modules/waiting-room/components/appointment-drawer/appointment-drawer-error-fallback"
import { useDrawerStore } from "@/stores/useDrawerStore"

const chAlternativeTriageDrawerQuery = graphql`
  query alternativeTriageDrawerChQuery($id: ID!) {
    ch_alternativeTriageById(id: $id) {
      reason
      timeWindowId
      timeWindow {
        id
        dateFrom
        dateTo
        referralReason
        referredBy
        referralToCareProviderId
        referralToCareProvider {
          id
        }
        referralToDoctorName
        internalComment
        referredByExternalCareProviderId
        updatedAt
        updatedBy
        createdAt
        createdBy
        progressNoteId
        errorCorrection
        referenceDate
        alternativeTriage
        involvesPreventionProgram
        alternativeTriageEntry {
          id
          phoneNumber
          reason
        }
      }
    }
  }
`

const deAlternativeTriageDrawerQuery = graphql`
  query alternativeTriageDrawerDeQuery($id: ID!) {
    de_alternativeTriageById(id: $id) {
      reason
    }
  }
`

const ChAlternativeTriageDrawerContent = ({
  selectedAlternativeTriageId,
  isEditMode,
}: {
  selectedAlternativeTriageId: string
  isEditMode: boolean
}) => {
  const { t } = useTranslation()
  const openDrawer = useDrawerStore((state) => state.openDrawer)

  const { ch_alternativeTriageById: alternativeTriageData } = useLazyLoadQuery<alternativeTriageDrawerChQuery>(
    chAlternativeTriageDrawerQuery,
    {
      id: selectedAlternativeTriageId,
    },
  )

  const handleViewTimeWindow = () => {
    const existingTimeWindow = alternativeTriageData?.timeWindow
    if (!existingTimeWindow) return

    const timeWindow: TimeWindowFormSchema = {
      id: existingTimeWindow.id,
      referredBy: existingTimeWindow.referredBy,
      referralToCareProviderId: existingTimeWindow.referralToCareProviderId ?? "",
      nameOfDoctor: existingTimeWindow.referralToDoctorName ?? "",
      referralReason:
        existingTimeWindow.referralReason === "REFERRAL"
          ? "APPOINTMENT_REGISTRATION"
          : existingTimeWindow.referralReason,
      dateFrom: new Date(existingTimeWindow.dateFrom),
      dateTo: new Date(existingTimeWindow.dateTo),
      internalComment: existingTimeWindow.internalComment ?? "",
      referredByExternalCareProviderId: existingTimeWindow.referredByExternalCareProviderId ?? "",
      updatedAt: existingTimeWindow.updatedAt ? new Date(existingTimeWindow.updatedAt) : undefined,
      updatedBy: existingTimeWindow.updatedBy ?? "",
      createdAt: existingTimeWindow.createdAt ? new Date(existingTimeWindow.createdAt) : undefined,
      createdBy: existingTimeWindow.createdBy ?? "",
      correction: existingTimeWindow.errorCorrection ? "timeWindowErrorCorrection" : "timeWindow",
      referenceDate: existingTimeWindow.referenceDate ? new Date(existingTimeWindow.referenceDate) : new Date(),
      alternativeTriage: existingTimeWindow.alternativeTriage ?? false,
      involvesPreventionProgram: existingTimeWindow.involvesPreventionProgram,
      alternativeTriagePhoneNumber: existingTimeWindow.alternativeTriageEntry?.phoneNumber,
      alternativeTriageReason: existingTimeWindow.alternativeTriageEntry?.reason,
    }
    const progressNoteId = existingTimeWindow.progressNoteId ?? null

    openDrawer(
      () => (
        <TimeWindowDrawer mode={isEditMode ? "EDIT" : "VIEW"} timeWindow={timeWindow} progressNoteId={progressNoteId} />
      ),
      "TimeWindowDrawer",
    )
  }

  return (
    <>
      <DrawerHeader className="mb-4 h-16 py-4.5 pr-4" data-testid="alternative-triage-drawer-head">
        <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued mb-15 text-sm font-medium">
          {t("patient.create.callReason.alternativeTriage.title")}
        </DrawerTitle>
      </DrawerHeader>
      <DrawerBody
        className="wrapper-container group/scrollbar grid grid-rows-1"
        data-testid="alternative-triage-drawer-body">
        <div className="h-full">
          {alternativeTriageData?.reason && (
            <>
              <div className="text-on-surface mb-3 text-base font-medium">
                {t("patient.agenda.grid.columnName.reasonForEncounter")}
              </div>
              <p className="text-sm">{alternativeTriageData?.reason}</p>
            </>
          )}
          {alternativeTriageData?.timeWindow && (
            <div className="mt-4 w-full">
              <div className="text-on-surface mb-3 text-base font-medium">{t("patient.agenda.timeWindow")}</div>
              <Button
                variant="secondary"
                className="mt-2 flex items-center gap-2"
                onClick={handleViewTimeWindow}
                data-testid="view-time-window-button">
                <ArrowSquareOut className="h-4 w-4" />
                {t("patient.agenda.alternativeTriage.viewTimeWindow", {
                  dateFrom: formatDate(new Date(alternativeTriageData.timeWindow.dateFrom), "dd.MM.yyyy"),
                  dateTo: formatDate(new Date(alternativeTriageData.timeWindow.dateTo), "dd.MM.yyyy"),
                })}
              </Button>
            </div>
          )}
        </div>
      </DrawerBody>
    </>
  )
}

const DeAlternativeTriageDrawerContent = ({ selectedAlternativeTriageId }: { selectedAlternativeTriageId: string }) => {
  const { t } = useTranslation()

  const { de_alternativeTriageById: alternativeTriageData } = useLazyLoadQuery<alternativeTriageDrawerDeQuery>(
    deAlternativeTriageDrawerQuery,
    {
      id: selectedAlternativeTriageId,
    },
  )

  return (
    <>
      <DrawerHeader className="mb-4 h-16 py-4.5 pr-4" data-testid="alternative-triage-drawer-head">
        <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued mb-15 text-sm font-medium">
          {t("patient.create.callReason.alternativeTriage.title")}
        </DrawerTitle>
      </DrawerHeader>
      <DrawerBody data-testid="agenda-appointment-drawer-body">
        <div className="h-full">
          {alternativeTriageData?.reason && (
            <>
              <div className="text-on-surface mb-3 text-base font-medium">
                {t("patient.agenda.grid.columnName.reasonForEncounter")}
              </div>
              <p className="text-sm">{alternativeTriageData?.reason}</p>
            </>
          )}
        </div>
      </DrawerBody>
    </>
  )
}

const AlternativeTriageDrawer = ({ selectedAlternativeTriageId }: { selectedAlternativeTriageId: string }) => {
  const drawerRef = useRef<HTMLDivElement>(null)
  const country = useCountryParam()
  const closeAlternativeTriageDrawer = useDrawerStore((store) => store.closeDrawer)
  const { isEditMode } = useOutletContext<{ isEditMode: boolean }>()

  useClickOutside((event: MouseEvent) => {
    if (drawerRef.current && !drawerRef.current.contains(event.target as HTMLElement)) {
      closeDrawer()
    }
  }, drawerRef)

  const closeDrawer = () => {
    handleToggleDrawer(true)
  }

  const handleToggleDrawer = useCallback(
    (close = false) => {
      if (drawerRef.current) {
        if (close) {
          drawerRef.current.classList.remove("w-125")
          drawerRef.current.classList.add("hidden", "w-0")
        } else {
          drawerRef.current.classList.add("w-125")
          drawerRef.current.classList.remove("hidden", "w-0")
        }
      }
    },
    [drawerRef],
  )

  return (
    <Drawer open onOpenChange={closeAlternativeTriageDrawer}>
      <DrawerContent className="shadow-1 relative border" ref={drawerRef} onOpenAutoFocus={(e) => e.preventDefault()}>
        <DrawerClose className="top-3 right-2" />
        <ErrorBoundary fallback={<ErrorFallback onClose={closeDrawer} />}>
          <Suspense>
            {country === "CH" ? (
              <ChAlternativeTriageDrawerContent
                selectedAlternativeTriageId={selectedAlternativeTriageId}
                isEditMode={isEditMode}
              />
            ) : (
              <DeAlternativeTriageDrawerContent selectedAlternativeTriageId={selectedAlternativeTriageId} />
            )}
          </Suspense>
        </ErrorBoundary>
      </DrawerContent>
    </Drawer>
  )
}

export { AlternativeTriageDrawer }
